import { useCallback, useEffect, useRef, useState } from 'react';

import { noop } from 'lodash-es';

import { FireOrderIn, IServerOrder } from '@rbi-ctg/menu';
import {
  GetOrderDocument,
  GetUserOrdersDocument,
  useCommitOrderMutation,
} from 'generated/graphql-gateway';
import { useOrderStatus } from 'hooks/order-status';
import { CustomEventNames, EventTypes, logRBIEvent } from 'state/amplitude';
import { useAuthContext } from 'state/auth';
import { useCartContext } from 'state/cart';
import { useFlyBuyContext } from 'state/flybuy';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { OrderInjectionFlowVariations } from 'state/launchdarkly/variations';
import { useOrderContext } from 'state/order';
import { ServiceMode } from 'state/service-mode';
import { buildCommitDeliveryInput } from 'utils/cart/build-commit-delivery-input';
import { buildCommitOrderInput } from 'utils/cart/build-commit-order-input';
import { PerformanceMarks, setMark } from 'utils/timing';

import { ICommitOrderInput, IUseCommitOrderParams } from './types';

// default order status polling interval, in ms
const DEFAULT_POLL_INTERVAL = 1500;

// FlyBuy orders will be created with 1h of fireOrderIn
const DEFAULT_FLYBUY_FIRE_ORDER_IN = 3600;

export const useCommitOrder = ({
  onCompleted = noop,
  orderStatusPollInterval = DEFAULT_POLL_INTERVAL,
  rbiOrderId,
  skipOrderStatusPolling = true,
  distanceToStore,
  checkoutData,
}: IUseCommitOrderParams) => {
  const [skipPolling, setSkipPolling] = useState(skipOrderStatusPolling);
  const { serviceMode, tipAmount } = useCartContext();
  const {
    onCommitSuccess,
    deliveryAddress,
    deliveryInstructions,
    orderPhoneNumber,
    fireOrderIn: orderContextFireOrderIn,
  } = useOrderContext();
  const { isReadyWithTimedFire: isFlyBuyReadyWithTimedFire } = useFlyBuyContext();

  const { user } = useAuthContext();
  const commitOptions = useRef({} as ICommitOrderInput);

  const [fireMutation, mutationResult] = useCommitOrderMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      { query: GetOrderDocument, variables: { id: rbiOrderId } },
      { query: GetUserOrdersDocument },
    ],
  });

  useEffect(() => {
    if (mutationResult.called && !mutationResult.loading) {
      let attributes;
      if (mutationResult.error) {
        attributes = {
          status: 'Failed',
          failureDetails: mutationResult.error.message,
        };
      } else {
        attributes = {
          status: mutationResult.data?.commitOrder.status || '',
        };
      }

      logRBIEvent(
        {
          name: CustomEventNames.SUBMIT_PAYMENT,
          type: EventTypes.Other,
          attributes,
        },
        { skipLoggingToBraze: true }
      );
    }
  }, [mutationResult.called, mutationResult.loading, mutationResult.data, mutationResult.error]);

  const onSuccess = useCallback(
    (rbiOrder: IServerOrder) => {
      onCompleted();
      onCommitSuccess(rbiOrder);
      setMark(PerformanceMarks.COMMIT_END);
    },
    [onCommitSuccess, onCompleted]
  );

  const queryResult = useOrderStatus({
    onSuccess,
    orderStatusPollInterval,
    rbiOrderId,
    skip: skipPolling,
  });

  const { orderStatus, serverOrder } = queryResult;

  const orderInjectFlow = useFlag(LaunchDarklyFlag.ORDER_INJECTION_FLOW);
  const isOrderTimedFire = orderInjectFlow === OrderInjectionFlowVariations.TIMED_FIRE;
  const isOrderInjectAndHold = orderInjectFlow === OrderInjectionFlowVariations.INJECT_AND_HOLD;

  const phoneNumber = orderPhoneNumber || user?.details.phoneNumber || '';
  const commitOrder = useCallback(
    async (options: ICommitOrderInput) => {
      commitOptions.current = options;
      const delivery = buildCommitDeliveryInput({
        deliveryAddress,
        deliveryInstructions,
        phoneNumber,
        serviceMode,
        tipAmount,
      });

      // If using Apple or Google Pay, get the underlying credit card used and set this as the card type used
      let fireOrderIn = (orderContextFireOrderIn || 0) as FireOrderIn;

      const isDriveThrough = serviceMode === ServiceMode.DRIVE_THRU;
      // for backwards compatibility purposes, we need to convert 0 to 1 for ASAP drive thru orders
      if (isDriveThrough && isOrderTimedFire && fireOrderIn === 0) {
        fireOrderIn = 1;
      }

      if (isOrderInjectAndHold) {
        fireOrderIn = null;
      }

      // If FlyBuy is ready and timed fire active set the fireOrderIn to 1h
      if (isFlyBuyReadyWithTimedFire) {
        fireOrderIn = DEFAULT_FLYBUY_FIRE_ORDER_IN;
      }

      const input = buildCommitOrderInput({
        ...options,
        fireOrderIn,
        rbiOrderId: rbiOrderId!,
        distanceToStore,
        checkoutData,
      });

      const skipCoolingPeriod = !!options.skipCoolingPeriod;
      setMark(PerformanceMarks.COMMIT_START);
      const result = await fireMutation({
        variables: {
          delivery,
          input,
          skipCoolingPeriod,
        },
      });
      if (result.data) {
        setSkipPolling(false);
      }
      return result;
    },
    [
      deliveryAddress,
      deliveryInstructions,
      phoneNumber,
      serviceMode,
      tipAmount,
      orderContextFireOrderIn,
      isOrderTimedFire,
      isFlyBuyReadyWithTimedFire,
      rbiOrderId,
      fireMutation,
      checkoutData,
    ]
  );

  const forceCommitOrder = useCallback(() => {
    return commitOrder({ ...commitOptions.current, skipCoolingPeriod: true });
  }, [commitOrder]);

  return {
    commitOrder,
    commitResult: mutationResult,
    failure: queryResult.failure,
    forceCommitOrder,
    loading: mutationResult.loading || queryResult.loading,
    order: queryResult,
    orderStatus,
    serverOrder,
    success: queryResult.success,
  };
};
